import { useTranslation } from 'next-i18next/pages';

import { Box, ColorSwatch } from '@highlight-cp/core-ui';

import classes from './status-color-dot.module.css';

/** How far along a piece of setup is. The shared vocabulary behind every status dot. */
export type ProgressStatus = 'notStarted' | 'draft' | 'ready';

/**
 * What each state is called. Exported so a control that announces the status itself — rather than
 * leaving it to the dot — reads from the same vocabulary.
 */
export const PROGRESS_STATUS_LABEL: Record<ProgressStatus, string> = {
  notStarted: 'Not started',
  draft: 'Drafting',
  ready: 'Ready',
};

const STATUS_COLOR: Record<ProgressStatus, string> = {
  notStarted: 'var(--mantine-color-gray-filled)',
  draft: 'var(--mantine-color-yellow-filled)',
  ready: 'var(--mantine-color-success)',
};

export interface StatusColorDotProps {
  /** The state to show. */
  status: ProgressStatus;
  /**
   * Diameter in pixels. `SidebarTabs` reserves a gutter for this default in its own stylesheet, so
   * changing it here means changing `.labelWithStatus` to match.
   *
   * @default 12
   */
  size?: number;
  /**
   * Keep the dot out of the accessibility tree. For use inside a control that already announces the
   * status itself — `SidebarTabs.Tab` describes itself from {@link PROGRESS_STATUS_LABEL} — where a
   * labelled dot would have the state read out twice.
   *
   * @default false
   */
  decorative?: boolean;
  /**
   * `trailing` keeps the gap that separates the dot from the label it follows. `leading` drops it,
   * for a dot that comes *before* its label, where the host provides the gap.
   *
   * @default 'trailing'
   */
  variant?: 'trailing' | 'leading';
}

/**
 * ### StatusColorDot
 *
 * @description
 * A small filled circle standing in for a progress state — gray not started, yellow drafting,
 * green ready. Use it where a status needs to sit beside a label without the weight of a
 * `StatusBadge` pill, as the project-setup sidebar tabs do.
 *
 * The colour is the only thing carrying the state, so the dot names itself: it renders as an
 * `img` with a translated label rather than relying on colour alone. Inside a control that already
 * announces its own status — `SidebarTabs.Tab` describes itself from
 * {@link PROGRESS_STATUS_LABEL} — hide the dot with `aria-hidden` on a wrapper instead, or the
 * state is read out twice.
 *
 * @example
 * ```tsx
 * <Flex align="center" gap="xs">
 *   <Text>Test Group 1</Text>
 *   <StatusColorDot status="draft" />
 * </Flex>
 * ```
 *
 * @param props - {@link StatusColorDotProps}
 * @returns React component
 */
export const StatusColorDot = ({
  status,
  size = 12,
  decorative = false,
  variant = 'trailing',
}: StatusColorDotProps) => {
  const { t } = useTranslation();

  return (
    // The name lives on the wrapper: ColorSwatch takes no `role`, and this keeps the swatch itself
    // out of the accessibility tree behind a single labelled image.
    <Box
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : t(PROGRESS_STATUS_LABEL[status])}
      className={classes.root}
      component="span"
      data-variant={variant}
      role={decorative ? undefined : 'img'}
    >
      {/* `span`, because the dot sits inside running text and Mantine's default `div` would nest a
          block element in the `p` a `Text` renders. The inset ring is for picker swatches; a status
          dot reads as a flat fill. */}
      <ColorSwatch color={STATUS_COLOR[status]} component="span" size={size} withShadow={false} />
    </Box>
  );
};
